import type { Workflow } from '@compass/client/types/workflow';
import momentTz from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import { DISPOSITION_REQUIRED_ACTIONS, HUMAN_CALL_COMPLETION_ACTIONS } from '@/constants/actions';
import { DISPOSITION_WORKFLOW_TYPES, WorkflowType } from '@/constants/dispositions';
import { useSelectableDispositions, useUpdateDispositionCode } from '@/hooks/useDispositions';
import { useStore } from '@/store/store';
import type { StatusUpdatePlan } from '@/utils/bulkStatusUpdate';
import {
  actionRequiresCaseNote,
  getActionConfirmationMessage,
  getActionLabel,
  SCHEDULABLE_ACTION_NAMES,
} from '@/utils/caseActions';
import { formatStatus, getStatusColors } from '@/utils/cases';
import { TIMEZONE_MAP } from '@/utils/date';
import { isSystemDispositionCode } from '@/utils/dispositions';

/** Preset scheduling options */
const SCHEDULE_PRESETS = [
  { label: 'Now', minutes: 0 },
  { label: '30 min', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '2 hours', minutes: 120 },
  { label: '4 hours', minutes: 240 },
] as const;

/**
 * Type-to-confirm guard for the irreversible close-case actions: the user must
 * type the action's own word before Confirm enables. A deliberate friction guard
 * against accidental case closure. `cancel_workflow` is here because current
 * PBM_V2_STATUS seeds name the Cancel Case / Mark as Terminated transition that
 * way — guarding only `cancel_case` would leave the same door open.
 */
const TYPED_CONFIRMATION_WORDS: Record<string, string> = {
  mark_as_completed: 'complete',
  mark_as_terminated: 'terminate',
  cancel_case: 'cancel',
  cancel_workflow: 'cancel',
  // Refill (JNJ-172): a skipped delivery ticket completes the case with nothing shipped.
  refill_human_audit_skip: 'skip',
  refill_mark_case_as_completed: 'complete',
};

/** Format a datetime-local string in the given IANA timezone */
function formatDatetimeLocalInTz(date: Date, ianaTz: string): string {
  const m = momentTz.tz(date, ianaTz);
  return m.format('YYYY-MM-DDTHH:mm');
}

/** Parse a datetime-local value as a time in the given IANA timezone and return UTC ISO string */
function datetimeLocalToUTC(datetimeLocal: string, ianaTz: string): string {
  return momentTz.tz(datetimeLocal, 'YYYY-MM-DDTHH:mm', ianaTz).utc().toISOString();
}

/** Format a Date for display in the given IANA timezone */
function formatDisplayDateInTz(date: Date, ianaTz: string): string {
  return momentTz.tz(date, ianaTz).format('MMM D, YYYY h:mm A');
}

interface StatusConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * `dispositionCode` is the numeric code the agent picked in the Case
   * Disposition selector below, when that selector was actually shown
   * (`showDisposition`) — undefined otherwise, or if nothing was ever
   * selected. This dialog already persists it onto the case's
   * `disposition_code` (see `handleConfirm`); callers that also need to write
   * it onto a specific call (e.g. finishing a human call, BI-1848) can use
   * this to do that themselves — this generic, shared dialog has no notion of
   * "the call" to write to.
   */
  onConfirm: (note?: string, scheduledAt?: string, dispositionCode?: number) => void;
  /**
   * Why the human-call step cannot be marked completed yet — the latest human
   * call carries no disposition. Mirrors the server guard
   * (service/calls.go guardHumanCallDispositionBeforeCompletion); computed by
   * `getHumanCallCompletionBlockReason`. Only the case-details page can supply
   * it, since only it loads the case's calls — the cases list leaves it unset
   * and relies on the server rejection.
   */
  callDispositionBlockReason?: string | null;
  /**
   * Whether the case's latest human call carries no disposition of its own.
   * Weaker than `callDispositionBlockReason`, which mirrors the server guard and
   * ignores calls that never reached a conversational status (not_started —
   * every human call's creation status — scheduled, cancelled). Supplied only by
   * the case-details page, which is the only host that loads the case's calls.
   */
  callMissingDisposition?: boolean;
  /**
   * Validate the case's output before the disposition is written, returning a message
   * to show instead of confirming, or null to proceed (LA-7358).
   *
   * Confirming here is the moment the disposition is persisted and the case is routed
   * on, so it is the last point at which a malformed value (today: phone/fax format)
   * can still be corrected cheaply. The check lives in the caller because only the
   * case-details page holds the schema and the live working output; this dialog is
   * shared with the cases list, which supplies nothing and keeps the old behaviour.
   *
   * Implementations are expected to also reveal the offending field behind the dialog,
   * so dismissing it lands the rep on the thing to fix.
   */
  validateBeforeConfirm?: () => string | null;
  statusUpdatePlans: StatusUpdatePlan[];
  workflow?: Workflow | null;
  isLoading?: boolean;
  cases?: any[]; // Add cases to get status information
  isIndividual?: boolean; // Flag to determine if it's individual or bulk
}

export function StatusConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  callDispositionBlockReason,
  callMissingDisposition = false,
  validateBeforeConfirm,
  statusUpdatePlans,
  workflow,
  isLoading = false,
  cases = [],
  isIndividual = false,
}: StatusConfirmationDialogProps) {
  const [note, setNote] = useState('');
  // Type-to-confirm guard for the terminal "complete case" action: the user must
  // type "complete" before the Confirm button is enabled.
  const [confirmText, setConfirmText] = useState('');
  const [selectedMinutes, setSelectedMinutes] = useState(0); // 0 = "Now"
  const [customDatetime, setCustomDatetime] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [selectedDispositionCode, setSelectedDispositionCode] = useState<number | ''>('');
  // Message from `validateBeforeConfirm`, shown in place of confirming (LA-7358).
  // Cleared on close and on the next attempt, so a fixed value re-enables Confirm
  // without the rep having to reopen the dialog.
  const [validationError, setValidationError] = useState<string | null>(null);
  const selectedWorkflow = useStore(state => state.getSelectedWorkflow());
  const selectedTimeZone = useStore(state => state.getTimeZone());
  const ianaTimeZone = TIMEZONE_MAP[selectedTimeZone];

  // For individual cases, get the action and case state for confirmation message
  const singlePlan = statusUpdatePlans[0];
  const singleCase =
    isIndividual && singlePlan ? cases.find(c => c.id === singlePlan.caseIds[0]) : null;

  // Check if this is a PQC action that requires a reason input
  const isPQCWorkflow = selectedWorkflow?.workflow_type === WorkflowType.PQC;
  const pqcReasonActions = ['cancel_case', 'cancel_workflow', 'mark_as_duplicate'];
  const isPQCReasonAction = isPQCWorkflow && pqcReasonActions.includes(singlePlan?.action || '');

  // For PQC: cancel actions require reason, mark_as_duplicate is optional
  const pqcCancelActions = ['cancel_case', 'cancel_workflow'];
  const isPQCReasonRequired =
    isPQCReasonAction && pqcCancelActions.includes(singlePlan?.action || '');

  // Check if this is a PBM or UnifiedMedBV workflow that should show disposition
  const workflowType = workflow?.workflow_type;
  const showDisposition =
    isIndividual &&
    DISPOSITION_WORKFLOW_TYPES.includes(workflowType ?? '') &&
    DISPOSITION_REQUIRED_ACTIONS.includes(singlePlan?.action || '');

  // Finishing the human call is blocked while the case's latest human call has
  // no disposition (mirrored from the server). The action name is
  // `human_call_mark_as_completed` for BOTH the common and the Medicare variant
  // — getWorkflowActionMap (caseActions.ts) only varies the resolved enum — so
  // one name covers both. Terminating is deliberately not gated.
  // The transportation and unifiedmedbv Program Admin's Move to Human Audit is gated the
  // same way (BV-2514, BV-2657).
  const blocksCompletion =
    isIndividual &&
    !!callDispositionBlockReason &&
    HUMAN_CALL_COMPLETION_ACTIONS.includes(singlePlan?.action || '');

  // The actions that end the human-call step (Mark Completed, Move to Human
  // Audit, Terminate) and, when a disposition is picked here, write it onto the
  // case's latest human call (BI-1848 — DefaultCaseDetailsPage
  // writeLatestHumanCallDisposition).
  const isHumanCallAction =
    HUMAN_CALL_COMPLETION_ACTIONS.includes(singlePlan?.action || '') ||
    singlePlan?.action === 'human_call_mark_as_terminated';

  // Whether this dialog must refuse the case's inherited disposition_code and
  // insist on a pick made HERE, in this session.
  //
  // Armed by `callMissingDisposition` — not just `blocksCompletion`. The
  // server-mirroring block reason ignores a human call still sitting at its
  // creation status (not_started, which is what service/calls.go CreateCall
  // writes), where an externally-dialled call or one whose terminal-status
  // webhook never landed stays forever. Meanwhile the case's own
  // disposition_code has usually just been auto-populated by
  // ACTION_CALCULATE_DISPOSITION when the agent left the Human Call tab, so the
  // required selector below silently satisfied itself from it and the case was
  // routed to human audit with the call left undispositioned.
  //
  // Terminate is armed too: it shows the same required selector and performs the
  // same call-level write. Requiring a pick from a dropdown already on screen
  // cannot wedge the step, which is what kept terminate ungated server-side.
  const mustPickDispositionAfresh =
    showDisposition &&
    (blocksCompletion || (isIndividual && isHumanCallAction && callMissingDisposition));

  // The case's saved disposition, as a value this dialog may actually offer.
  //
  // A SYSTEM code (101-106, 0 — see `isSystemDispositionCode`) is discarded
  // here rather than carried through. Those are telephony outcomes stamped by
  // `case_sync` when a dial fails, not benefit outcomes anyone chose, and none
  // of them exist in a workflow's selectable catalog — so the dropdown has no
  // option to render and falls back to its placeholder, leaving the field
  // looking empty while holding a value that satisfies every gate below.
  // Treating it as absent is what makes "looks empty" and "is empty" agree.
  const rawCaseDispositionCode = singleCase?.disposition_code as number | undefined;
  const currentDispositionCode = isSystemDispositionCode(rawCaseDispositionCode)
    ? undefined
    : rawCaseDispositionCode;

  // Resolve dispositions from the @100mslive/disposition-engine package.
  // Selectable-only: retired codes are not offered, except the one this case is
  // already set to (so the control shows its current value instead of blank).
  // Passed the SANITISED code: asking the engine to keep a system code
  // selectable would put "Not Connected - Call rejected or busy signal" in a
  // benefit-disposition dropdown.
  const { data: dispositions = [], isLoading: isLoadingDispositions } = useSelectableDispositions(
    showDisposition ? workflowType : undefined,
    currentDispositionCode,
  );

  // Mutation to persist a newly-selected disposition before confirming the action
  const updateDispositionCode = useUpdateDispositionCode(singlePlan?.caseIds[0] || '');

  // Sync the local selector to the case's saved disposition whenever the dialog
  // opens or the underlying case changes.
  //
  // Except when a fresh pick is required: seeding the case's code here made
  // `selectedDispositionCode` a number the moment the dialog opened, which is
  // exactly what `needsFreshDispositionPick` tests for — so the requirement
  // could never fire, and `handleConfirm` forwarded the inherited code as if the
  // agent had chosen it. Start blank instead, so the pick is real.
  useEffect(() => {
    if (isOpen) {
      setSelectedDispositionCode(mustPickDispositionAfresh ? '' : (currentDispositionCode ?? ''));
    }
  }, [isOpen, currentDispositionCode, mustPickDispositionAfresh]);

  // Convert dispositions to select options
  const dispositionOptions = useMemo(
    () => dispositions.map(d => ({ value: d.code || 0, label: d.label || '' })),
    [dispositions],
  );

  // Effective disposition for gating Confirm — what the user has selected (or
  // the existing value if untouched)
  const effectiveDispositionCode =
    selectedDispositionCode !== '' ? selectedDispositionCode : currentDispositionCode;

  // When the call itself has no disposition, `effectiveDispositionCode`'s
  // fallback to the case's saved disposition_code is not enough: Confirm would
  // pass without `onConfirm` receiving a code, so nothing is ever written to
  // the call (BI-1848's write is driven by that argument). Require a real pick
  // from this session instead — see `mustPickDispositionAfresh`.
  const needsFreshDispositionPick =
    mustPickDispositionAfresh && typeof selectedDispositionCode !== 'number';

  // No selector for this workflow, so the dialog itself cannot resolve the
  // block — the agent has to set the call's disposition in Review first.
  const completionBlockedWithNoFix = blocksCompletion && !showDisposition;

  // Show notes for non-PQC workflows, or show reason input for PQC cancel/duplicate actions
  const showNotes = !isPQCWorkflow;
  const showReasonInput = isPQCReasonAction;

  const totalCases = statusUpdatePlans.reduce((total, plan) => total + plan.count, 0);

  // Check if any action in the plans requires a case note (for non-PQC workflows)
  const isNoteRequired = statusUpdatePlans.some(plan => actionRequiresCaseNote(plan.action));

  // First plan whose action is guarded decides the word to type (see
  // TYPED_CONFIRMATION_WORDS); unguarded actions ask for nothing.
  const typedConfirmationWord =
    statusUpdatePlans.map(plan => TYPED_CONFIRMATION_WORDS[plan.action]).find(Boolean) ?? '';
  const requiresTypedConfirmation = typedConfirmationWord !== '';
  const typedConfirmationSatisfied =
    !requiresTypedConfirmation || confirmText.trim().toLowerCase() === typedConfirmationWord;
  // Cancelling reads differently from terminating in the note prompt, and the
  // footer's own "Cancel" button would otherwise sit right under "type cancel
  // to confirm".
  const isCancelAction = typedConfirmationWord === 'cancel';

  // Scheduling support — only for recall_scheduler and referral_scheduler workflows
  const isSchedulingWorkflow =
    workflowType === WorkflowType.RECALL_SCHEDULER ||
    workflowType === WorkflowType.REFERRAL_SCHEDULER;
  const showScheduling =
    isIndividual &&
    isSchedulingWorkflow &&
    (SCHEDULABLE_ACTION_NAMES as readonly string[]).includes(singlePlan?.action || '');
  const workflowExpiresAt = singleCase?.workflow_expires_at
    ? new Date(singleCase.workflow_expires_at)
    : null;

  // Compute min/max datetime for the custom picker in the selected timezone
  const nowDatetime = useMemo(
    () => formatDatetimeLocalInTz(new Date(), ianaTimeZone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, ianaTimeZone],
  );
  const maxDatetime = workflowExpiresAt
    ? formatDatetimeLocalInTz(workflowExpiresAt, ianaTimeZone)
    : undefined;

  // Check if a preset would exceed the workflow expiry
  const isPresetDisabled = (minutes: number): boolean => {
    if (minutes === 0 || !workflowExpiresAt) return false;
    const scheduledTime = new Date(Date.now() + minutes * 60 * 1000);
    return scheduledTime > workflowExpiresAt;
  };

  // Check if custom datetime exceeds workflow expiry (compare in UTC)
  const isCustomDatetimeInvalid =
    useCustom &&
    customDatetime &&
    workflowExpiresAt &&
    momentTz.tz(customDatetime, 'YYYY-MM-DDTHH:mm', ianaTimeZone).toDate() > workflowExpiresAt;

  // Resolve the scheduled_at UTC ISO string from current selection
  const resolveScheduledAt = (): string | undefined => {
    if (!showScheduling) return undefined;
    if (useCustom) {
      return customDatetime ? datetimeLocalToUTC(customDatetime, ianaTimeZone) : undefined;
    }
    if (selectedMinutes === 0) return undefined; // "Now" = no scheduled_at
    return new Date(Date.now() + selectedMinutes * 60 * 1000).toISOString();
  };

  const resetState = () => {
    setNote('');
    setConfirmText('');
    setSelectedMinutes(0);
    setCustomDatetime('');
    setUseCustom(false);
    setSelectedDispositionCode('');
    setValidationError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = async () => {
    // Validate the output BEFORE anything is written (LA-7358). Confirming persists
    // the disposition and routes the case on, so this is the last cheap moment to
    // catch a malformed value — and it runs ahead of `updateDispositionCode` so a
    // failed check leaves the case exactly as it was.
    const message = validateBeforeConfirm?.() ?? null;
    setValidationError(message);
    if (message) return;

    // If the user picked a different disposition than the case currently has,
    // save it before firing the action — terminate is gated on disposition.
    if (
      showDisposition &&
      selectedDispositionCode !== '' &&
      selectedDispositionCode !== currentDispositionCode
    ) {
      try {
        await updateDispositionCode.mutateAsync(selectedDispositionCode);
      } catch {
        // useUpdateDispositionCode already surfaces a toast on error.
        return;
      }
    }
    onConfirm(
      note.trim() || undefined,
      resolveScheduledAt(),
      // Only a real selection from the visible selector counts — never the
      // sync effect's blank default, and never leaked when the selector
      // wasn't shown at all for this action/workflow.
      showDisposition && typeof selectedDispositionCode === 'number'
        ? selectedDispositionCode
        : undefined,
    );
    resetState();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isIndividual ? 'Confirm Action' : 'Confirm Status Changes'}</DialogTitle>
          <DialogDescription>
            {isIndividual
              ? getActionConfirmationMessage(singlePlan?.action || '', singleCase?.state)
              : `You are about to update ${totalCases} case${totalCases === 1 ? '' : 's'}`}
          </DialogDescription>
        </DialogHeader>

        {/* Show different content based on individual vs bulk */}
        {!isIndividual && (
          // Detailed cards for bulk operations
          <div className="space-y-4 py-4">
            {statusUpdatePlans.map((plan, index) => {
              // Find the first case to get the current status
              const sampleCase = cases.find(c => plan.caseIds.includes(c.id!));
              const fromStatus = sampleCase?.state || 'unknown';

              // Get status colors for from and to states
              const fromStatusColors = getStatusColors(fromStatus, workflow);
              const displayLabel = fromStatusColors?.label || formatStatus(fromStatus, workflow);

              return (
                <div key={index} className="border rounded-lg p-3 space-y-3">
                  {/* Header section showing status and case count */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{displayLabel}</span>
                    <span className="text-sm text-muted-foreground font-medium">
                      {plan.count} case{plan.count === 1 ? '' : 's'}
                    </span>
                  </div>

                  {/* Action section showing transition */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3">
                        {fromStatusColors ? (
                          <div
                            className={`${fromStatusColors.bg} ${fromStatusColors.text} ${fromStatusColors.border} border inline-flex items-center px-3 py-1 rounded text-xs font-medium whitespace-nowrap`}
                          >
                            {displayLabel}
                          </div>
                        ) : (
                          <span className="bg-muted text-muted-foreground px-3 py-1 rounded-md text-sm font-medium">
                            {fromStatus}
                          </span>
                        )}
                        <span className="text-muted-foreground text-lg">→</span>
                      </div>

                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {getActionLabel(plan.action, fromStatus)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Case Disposition selector for PBM/UnifiedMedBV/Transportation workflows */}
        {isIndividual && showDisposition && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              Case Disposition <span className="text-destructive">*</span>
            </Label>
            <SearchableSelect
              // Show what Confirm is actually gated on, not just this session's
              // pick. Both read effectiveDispositionCode, which falls back to
              // the case's existing disposition_code — binding the display to
              // the raw selection instead left a case that already had one
              // rendering an empty required field beside an enabled Confirm.
              //
              // When a fresh pick is required that fallback is precisely what is
              // being refused, so the raw selection IS what Confirm is gated on.
              // Showing the inherited label there would also be a trap: this
              // control toggles, so clicking the already-displayed option clears
              // it instead of confirming it.
              value={
                mustPickDispositionAfresh
                  ? selectedDispositionCode
                  : (effectiveDispositionCode ?? '')
              }
              onValueChange={value =>
                setSelectedDispositionCode(value === '' ? '' : (value as number))
              }
              options={dispositionOptions}
              placeholder={
                isLoadingDispositions
                  ? 'Loading dispositions...'
                  : updateDispositionCode.isPending
                    ? 'Saving...'
                    : 'Select disposition...'
              }
              searchPlaceholder="Search dispositions..."
              disabled={isLoadingDispositions || updateDispositionCode.isPending || isLoading}
              error={!effectiveDispositionCode || needsFreshDispositionPick}
            />
            {needsFreshDispositionPick && (
              <p className="text-xs text-destructive">
                {callDispositionBlockReason ??
                  'Select a disposition for the call before ending the human call.'}
              </p>
            )}
          </div>
        )}

        {/* The latest human call has no disposition and this workflow offers no
            selector above, so the block can only be cleared from the Review
            tab. Rendered as plain in-dialog text, not a `title` on the disabled
            Confirm button: the shadcn Button base sets
            `disabled:pointer-events-none`, so a disabled button never receives
            hover (BV-1937). */}
        {completionBlockedWithNoFix && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
            <p className="text-xs text-destructive">
              {callDispositionBlockReason} Set it on the call in the Review tab, then try again.
            </p>
          </div>
        )}

        {/* Scheduling section for call_with_ai / call_with_human actions */}
        {showScheduling && (
          <div className="space-y-3">
            <Label className="text-sm text-muted-foreground">
              Schedule call ({selectedTimeZone})
            </Label>
            <div className="flex flex-wrap gap-2">
              {SCHEDULE_PRESETS.map(preset => {
                const disabled = isPresetDisabled(preset.minutes);
                const isSelected = !useCustom && selectedMinutes === preset.minutes;
                return (
                  <button
                    key={preset.minutes}
                    type="button"
                    aria-pressed={isSelected}
                    disabled={disabled || isLoading}
                    onClick={() => {
                      setUseCustom(false);
                      setSelectedMinutes(preset.minutes);
                      setCustomDatetime('');
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : disabled
                          ? 'bg-muted text-muted-foreground border-border opacity-50 cursor-not-allowed'
                          : 'bg-background text-foreground border-border hover:bg-accent'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
              <button
                type="button"
                aria-pressed={useCustom}
                disabled={isLoading}
                onClick={() => {
                  setUseCustom(true);
                  setSelectedMinutes(0);
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  useCustom
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-accent'
                }`}
              >
                Custom
              </button>
            </div>

            {/* Custom datetime picker */}
            {useCustom && (
              <div className="space-y-1.5">
                <input
                  type="datetime-local"
                  value={customDatetime}
                  min={nowDatetime}
                  max={maxDatetime}
                  onChange={e => setCustomDatetime(e.target.value)}
                  disabled={isLoading}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                {isCustomDatetimeInvalid && (
                  <p className="text-xs text-destructive">Must be before workflow expiry</p>
                )}
              </div>
            )}

            {/* Workflow expiry info */}
            {workflowExpiresAt && (
              <p className="text-xs text-muted-foreground">
                Workflow expires {formatDisplayDateInTz(workflowExpiresAt, ianaTimeZone)}{' '}
                {selectedTimeZone}
              </p>
            )}
          </div>
        )}

        {/* Type-to-confirm guard for terminal close-case actions (complete / terminate) */}
        {requiresTypedConfirmation && (
          <div className="space-y-2">
            <Label htmlFor="confirm-action" className="text-sm text-muted-foreground">
              Type <span className="font-semibold text-foreground">{typedConfirmationWord}</span> to
              confirm <span className="text-destructive">*</span>
            </Label>
            <Input
              id="confirm-action"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              autoComplete="off"
              disabled={isLoading}
            />
          </div>
        )}

        {/* Note input (optional/last) - hidden for PQC workflow */}
        {showNotes && (
          <div className="space-y-2">
            <Label htmlFor="note" className="text-sm text-muted-foreground">
              Add a note{' '}
              {isNoteRequired ? <span className="text-destructive">*</span> : '(optional)'}
            </Label>
            <Textarea
              id="note"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={
                isNoteRequired
                  ? isCancelAction
                    ? 'Please provide a reason for cancellation...'
                    : 'Please provide a reason for termination...'
                  : 'Add context about this status change...'
              }
              className="min-h-[80px] resize-none"
              disabled={isLoading}
            />
          </div>
        )}

        {/* Reason input for PQC cancel/duplicate actions */}
        {showReasonInput && (
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm text-muted-foreground">
              Reason{' '}
              {isPQCReasonRequired ? <span className="text-destructive">*</span> : '(optional)'}
            </Label>
            <Textarea
              id="reason"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={
                isPQCReasonRequired
                  ? 'Please provide a reason for cancellation...'
                  : 'Optionally provide a reason...'
              }
              className="min-h-[80px] resize-none"
              disabled={isLoading}
            />
          </div>
        )}

        {/* Why Confirm did nothing (LA-7358). Sits above the footer rather than beside
            the button so the full message wraps instead of squeezing the buttons, and
            the caller has already revealed the offending field behind the dialog. */}
        {validationError && (
          <p className="text-sm text-destructive" role="alert">
            {validationError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {isCancelAction ? 'Go back' : 'Cancel'}
          </Button>
          <Button
            data-analytics-id={isIndividual ? 'case:status:confirm' : 'case:status:bulk-confirm'}
            onClick={handleConfirm}
            disabled={
              isLoading ||
              updateDispositionCode.isPending ||
              !typedConfirmationSatisfied ||
              (showNotes && isNoteRequired && !note.trim()) ||
              (showReasonInput && isPQCReasonRequired && !note.trim()) ||
              (showDisposition && !effectiveDispositionCode) ||
              needsFreshDispositionPick ||
              completionBlockedWithNoFix ||
              (showScheduling && useCustom && !customDatetime) ||
              !!isCustomDatetimeInvalid
            }
          >
            {isLoading || updateDispositionCode.isPending
              ? 'Updating...'
              : showScheduling && (useCustom || selectedMinutes > 0)
                ? 'Schedule'
                : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
